import { useState, useEffect, useRef } from "react";
import { storageManager } from '../utils/storage';
import PromptForm from "./PromptForm";
import PromptList from "./PromptList";
import Modal from "./Modal";
import ConfirmModal from "./ConfirmModal";
import ImportConfirmModal from "./ImportConfirmModal";
import TagSelector from "./TagSelector";
import { PromptItem, Category } from "../utils/types";
import { DEFAULT_CATEGORY_ID } from "../utils/constants";
import { getCategories, migratePromptsWithCategory, saveCategories } from "../utils/categoryUtils";
import { toast } from "../hooks/useToast";

type SourceFilter = 'all' | 'local' | 'remote';

const PromptManager = () => {
  const [prompts, setPrompts] = useState<PromptItem[]>([]);
  const [filteredPrompts, setFilteredPrompts] = useState<PromptItem[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFilterTags, setSelectedFilterTags] = useState<string[]>([]);
  const [selectedSourceFilter, setSelectedSourceFilter] = useState<SourceFilter>('all');
  const [editingPrompt, setEditingPrompt] = useState<PromptItem | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [initialContent, setInitialContent] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [promptToDelete, setPromptToDelete] = useState<string | null>(null);

  // 添加分类相关状态
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);

  // 远程同步相关状态
  const [isRemoteSyncing, setIsRemoteSyncing] = useState(false);

  // 导入确认弹窗相关状态
  const [isImportConfirmOpen, setIsImportConfirmOpen] = useState(false);
  const [pendingImportData, setPendingImportData] = useState<{
    categories: Category[];
    prompts: PromptItem[];
    isRemoteSync: boolean;
  } | null>(null);

  // Load prompts and categories from storage
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);

        // 先迁移旧数据
        await migratePromptsWithCategory();

        // 加载提示词
        const storedPrompts = await storageManager.getPrompts();

        // 按照 sortOrder 排序，置顶项目优先
        const sortedPrompts = (storedPrompts || []).sort((a, b) => {
          // 置顶项目始终在前面
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;

          // 在同一置顶状态下，按照 sortOrder 排序
          const aOrder = a.sortOrder !== undefined ? a.sortOrder : 999999;
          const bOrder = b.sortOrder !== undefined ? b.sortOrder : 999999;
          return aOrder - bOrder;
        });

        setPrompts(sortedPrompts);

        // 加载分类
        const storedCategories = await getCategories();
        setCategories(storedCategories);

        console.log('加载提示词数量:', storedPrompts?.length || 0);
        console.log('加载分类数量:', storedCategories.length);
      } catch (err) {
        console.error('加载数据错误:', err);
        setError('加载数据失败');
      } finally {
        setIsLoading(false);
      }
    };

    loadData();
  }, []);

  // Filter prompts based on search term and selected category
  useEffect(() => {
    let filtered = [...prompts];

    // 先按分类筛选
    if (selectedCategoryId) {
      filtered = filtered.filter(prompt => prompt.categoryId === selectedCategoryId);
    }

    // 按数据来源筛选
    if (selectedSourceFilter !== 'all') {
      filtered = filtered.filter(prompt => (prompt.source || 'local') === selectedSourceFilter);
    }

    // 按标签筛选
    if (selectedFilterTags.length > 0) {
      filtered = filtered.filter(prompt =>
        selectedFilterTags.every(filterTag =>
          prompt.tags.some(tag => tag.toLowerCase() === filterTag.toLowerCase())
        )
      );
    }

    // 再按搜索词筛选
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((prompt) => {
        const titleMatch = prompt.title.toLowerCase().includes(term);
        const contentMatch = prompt.content.toLowerCase().includes(term);
        const tagMatch = prompt.tags.some((tag) =>
          tag.toLowerCase().includes(term)
        );
        return titleMatch || contentMatch || tagMatch;
      });
    }

    // 按置顶状态和排序号排序：置顶的在前面，同级别内按 sortOrder 升序
    filtered.sort((a, b) => {
      // 首先按置顶状态排序，置顶的在前面
      if (a.pinned && !b.pinned) return -1;
      if (!a.pinned && b.pinned) return 1;

      // 如果置顶状态相同，按 sortOrder 升序排序
      const aOrder = a.sortOrder !== undefined ? a.sortOrder : 999999;
      const bOrder = b.sortOrder !== undefined ? b.sortOrder : 999999;
      return aOrder - bOrder;
    });

    setFilteredPrompts(filtered);
  }, [searchTerm, prompts, selectedCategoryId, selectedFilterTags, selectedSourceFilter]);

  // Get all available tags from prompts
  const getAllTags = () => {
    const tagSet = new Set<string>();
    prompts.forEach(prompt => {
      prompt.tags.forEach(tag => {
        if (tag.trim()) {
          tagSet.add(tag.trim());
        }
      });
    });
    return Array.from(tagSet).sort();
  };

  // Save prompts to storage
  const savePrompts = async (newPrompts: PromptItem[]) => {
    try {
      await storageManager.savePrompts(newPrompts);
      console.log('提示词已保存');
      setPrompts(newPrompts);
    } catch (err) {
      console.error('保存提示词错误:', err);
      setError('保存提示词失败');
    }
  };

  // Add a new prompt
  const addPrompt = async (prompt: Omit<PromptItem, "id">) => {
    const newPrompt: PromptItem = {
      ...prompt,
      id: crypto.randomUUID(),
      enabled: prompt.enabled !== undefined ? prompt.enabled : true,
      lastModified: prompt.lastModified || new Date().toISOString(),
      source: 'local', // 标记为本地创建
    };

    const newPrompts = [newPrompt, ...prompts];
    await savePrompts(newPrompts);
  };

  // Update an existing prompt
  const updatePrompt = async (updatedPrompt: PromptItem) => {
    const newPrompts = prompts.map((p) =>
      p.id === updatedPrompt.id ? updatedPrompt : p
    );

    await savePrompts(newPrompts);
    setEditingPrompt(null);
  };

  // Handle form submission for both add and update operations
  const handlePromptSubmit = async (
    prompt: PromptItem | Omit<PromptItem, "id">
  ) => {
    if ("id" in prompt && prompt?.id) {
      await updatePrompt(prompt as PromptItem);
    } else {
      await addPrompt(prompt);
    }

    closeModal();
  };

  // Delete a prompt
  const deletePrompt = async (id: string) => {
    setPromptToDelete(id);
    setIsConfirmModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (promptToDelete) {
      // 从数据库中删除提示词
      await storageManager.deletePrompt(promptToDelete);

      // 更新本地状态
      const newPrompts = prompts.filter((p) => p.id !== promptToDelete);
      setPrompts(newPrompts);

      if (editingPrompt?.id === promptToDelete) {
        setEditingPrompt(null);
      }
    }

    setPromptToDelete(null);
    setIsConfirmModalOpen(false);
  };

  // 远程同步API地址常量
  const REMOTE_SYNC_API = 'https://api.xiaojingge.com/api/api/prompts';

  // 远程同步函数
  const syncFromRemoteApi = async () => {
    try {
      setIsRemoteSyncing(true);
      setError(null);

      // 发起网络请求
      const response = await fetch(REMOTE_SYNC_API, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`网络请求失败: ${response.status} ${response.statusText}`);
      }

      // 解析API响应
      const apiResponse = await response.json();

      // 验证响应格式
      if (apiResponse.code !== 200 || !apiResponse.data) {
        throw new Error(apiResponse.message || '远程同步失败');
      }

      // 提取数据
      const importedData = apiResponse.data;
      let importedCategories: Category[] = [];
      let importedPrompts: PromptItem[] = [];

      if (Array.isArray(importedData.categories)) {
        importedCategories = importedData.categories;
      }
      if (Array.isArray(importedData.prompts)) {
        importedPrompts = importedData.prompts;
      }

      // 验证提示词数据格式
      const validPrompts = importedPrompts.filter((prompt) => {
        return (
          typeof prompt === 'object' &&
          typeof prompt.id === 'string' &&
          typeof prompt.title === 'string' &&
          typeof prompt.content === 'string' &&
          Array.isArray(prompt.tags)
        );
      }).map((prompt) => ({
        ...prompt,
        categoryId: prompt.categoryId || DEFAULT_CATEGORY_ID,
        enabled: prompt.enabled !== undefined ? prompt.enabled : true,
        lastModified: prompt.lastModified || new Date().toISOString(),
        notes: prompt.notes || '',
        thumbnail: typeof prompt.thumbnail === 'string' ? prompt.thumbnail.trim() : '',
        tags: prompt.tags || [],
        source: 'remote' as const, // 标记为远程同步数据
      }));

      // 验证分类数据格式
      const validCategories = importedCategories.filter((category) => {
        return (
          typeof category === 'object' &&
          typeof category.id === 'string' &&
          typeof category.name === 'string'
        );
      }).map((category) => ({
        ...category,
        description: category.description || '',
        color: category.color || '#6366f1',
        enabled: category.enabled !== undefined ? category.enabled : true,
        createdAt: category.createdAt || new Date().toISOString(),
        updatedAt: category.updatedAt || new Date().toISOString(),
        source: 'remote' as const, // 标记为远程同步数据
      }));

      if (validPrompts.length === 0 && validCategories.length === 0) {
        throw new Error('远程数据为空，无需同步');
      }

      // 设置待导入数据并显示确认弹窗
      setPendingImportData({
        categories: validCategories,
        prompts: validPrompts,
        isRemoteSync: true,
      });
      setIsImportConfirmOpen(true);

    } catch (err) {
      console.error('远程同步错误:', err);
      setError(err instanceof Error ? err.message : '远程同步失败');
    } finally {
      setIsRemoteSyncing(false);
    }
  };

  // Start editing a prompt
  const startEdit = (id: string) => {
    const prompt = prompts.find((p) => p.id === id);
    if (prompt) {
      setEditingPrompt(prompt);
      setIsModalOpen(true);
    }
  };

  // Cancel editing
  const cancelEdit = () => {
    setEditingPrompt(null);
    setInitialContent(null);
    closeModal();
  };

  // Open modal for adding a new prompt
  const openAddModal = () => {
    setEditingPrompt(null);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = () => {
    setIsModalOpen(false);
    setInitialContent(null);
  };


  // 添加切换启用状态的函数
  const togglePromptEnabled = async (id: string, enabled: boolean) => {
    const newPrompts = prompts.map((p) =>
      p.id === id ? { ...p, enabled } : p
    );
    await savePrompts(newPrompts);
  };

  // 添加切换置顶状态的函数
  const togglePromptPinned = async (id: string, pinned: boolean) => {
    const newPrompts = prompts.map((p) =>
      p.id === id ? { ...p, pinned } : p
    );
    await savePrompts(newPrompts);
  };

  // 添加拖拽排序处理函数
  const handleReorder = async (activeId: string, overId: string) => {
    const oldIndex = prompts.findIndex(p => p.id === activeId);
    const newIndex = prompts.findIndex(p => p.id === overId);

    if (oldIndex === -1 || newIndex === -1) return;

    // 重新排列数组
    const newPrompts = [...prompts];
    const [removed] = newPrompts.splice(oldIndex, 1);
    newPrompts.splice(newIndex, 0, removed);

    // 更新 sortOrder 字段
    const updatedPrompts = newPrompts.map((prompt, index) => ({
      ...prompt,
      sortOrder: index,
      lastModified: new Date().toISOString()
    }));

    await savePrompts(updatedPrompts);
  };

  // 导出分类和提示词
  const exportPrompts = () => {
    if (prompts.length === 0 && categories.length === 0) {
      toast.warning('没有数据可导出', 3000);
      return;
    }

    try {
      const exportData = {
        categories: categories,
        prompts: prompts,
        exportDate: new Date().toISOString(),
        version: '1.0'
      };

      const dataStr = JSON.stringify(exportData, null, 2);
      const dataBlob = new Blob([dataStr], { type: "application/json" });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `prompt-data-export-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      console.log('导出成功');
    } catch (err) {
      console.error('导出错误:', err);
      setError('导出失败');
    }
  };

  // 触发文件选择对话框
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 导入分类和提示词
  const importPrompts = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      const fileContent = await file.text();
      const importedData = JSON.parse(fileContent);

      // 验证导入的数据格式
      if (!importedData || typeof importedData !== 'object') {
        throw new Error('无效的文件格式');
      }

      // 支持新格式（包含categories和prompts）和旧格式（只有prompts数组）
      let importedCategories: Category[] = [];
      let importedPrompts: PromptItem[] = [];

      if (Array.isArray(importedData)) {
        // 旧格式：只有prompts数组
        importedPrompts = importedData;
      } else if (importedData.categories && importedData.prompts) {
        // 新格式：包含categories和prompts
        importedCategories = importedData.categories || [];
        importedPrompts = importedData.prompts || [];
      } else {
        throw new Error('不支持的文件格式，请确保文件包含有效的提示词数据');
      }

      // 验证分类数据
      const validCategories = importedCategories.filter((category) => {
        return (
          typeof category === "object" &&
          typeof category.id === "string" &&
          typeof category.name === "string" &&
          typeof category.enabled === "boolean"
        );
      }).map((category) => ({
        ...category,
        description: category.description || "",
        color: category.color || "#6366f1",
        createdAt: category.createdAt || new Date().toISOString(),
        updatedAt: category.updatedAt || new Date().toISOString(),
        source: (category.source || 'local') as 'local' | 'remote', // 保留原有source或标记为local
      }));

      // 验证提示词数据
      const validPrompts = importedPrompts.filter((prompt) => {
        return (
          typeof prompt === "object" &&
          typeof prompt.id === "string" &&
          typeof prompt.title === "string" &&
          typeof prompt.content === "string"
        );
      }).map((prompt) => ({
        ...prompt,
        enabled: prompt.enabled !== undefined ? prompt.enabled : true,
        tags: Array.isArray(prompt.tags) ? prompt.tags : [],
        notes: prompt.notes || "",
        categoryId: prompt.categoryId || DEFAULT_CATEGORY_ID,
        lastModified: prompt.lastModified || new Date().toISOString(),
        thumbnail: typeof prompt.thumbnail === 'string' ? prompt.thumbnail.trim() : '',
        source: (prompt.source || 'local') as 'local' | 'remote', // 保留原有source或标记为local
      }));

      if (validCategories.length === 0 && validPrompts.length === 0) {
        throw new Error('文件中没有有效的数据');
      }

      // 设置待导入数据并显示确认弹窗
      setPendingImportData({
        categories: validCategories,
        prompts: validPrompts,
        isRemoteSync: false,
      });
      setIsImportConfirmOpen(true);

      // 清除文件输入
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (err) {
      console.error('导入错误:', err);
      setError(`导入失败: ${err instanceof Error ? err.message : '未知错误'}`);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  // 确认导入数据
  const handleConfirmImport = async () => {
    if (!pendingImportData) return;

    const { categories: validCategories, prompts: validPrompts, isRemoteSync } = pendingImportData;

    try {
      let importResults = [];

      // 处理分类数据
      if (validCategories.length > 0) {
        let addedCategoriesCount = 0;
        let updatedCategoriesCount = 0;
        let deletedCategoriesCount = 0;

        if (isRemoteSync) {
          // 远程同步：保留本地数据，删除旧的远程数据，添加新的远程数据
          const localCategories = categories.filter(c => c.source === 'local');
          const oldRemoteCategoryIds = categories.filter(p => p.source === 'remote').map(p => p.id);
          const newRemoteCategoryIds = validCategories.map(p => p.id);
          updatedCategoriesCount = newRemoteCategoryIds.filter(id => oldRemoteCategoryIds.includes(id)).length;

          if(newRemoteCategoryIds.length > oldRemoteCategoryIds.length) {
            addedCategoriesCount = newRemoteCategoryIds.length - oldRemoteCategoryIds.length;
          } else {
            deletedCategoriesCount = oldRemoteCategoryIds.length - newRemoteCategoryIds.length;
          }
          const categoriesMap = new Map(localCategories.map(c => [c.id, c]));

          validCategories.forEach(category => {
            categoriesMap.set(category.id, category);
          });

          const newCategories = Array.from(categoriesMap.values());
          setCategories(newCategories);
          await saveCategories(newCategories);

          if (addedCategoriesCount > 0 || updatedCategoriesCount > 0 || deletedCategoriesCount > 0) {
            const parts = [];
            if (addedCategoriesCount > 0) parts.push(`新增 ${addedCategoriesCount} 个`);
            if (updatedCategoriesCount > 0) parts.push(`更新 ${updatedCategoriesCount} 个`);
            if (deletedCategoriesCount > 0) parts.push(`删除 ${deletedCategoriesCount} 个`);
            importResults.push(`分类：${parts.join('，')}`);
          }
        } else {
          // 本地导入：普通合并逻辑
          const categoriesMap = new Map(categories.map(c => [c.id, c]));

          validCategories.forEach(category => {
            if (categoriesMap.has(category.id)) {
              categoriesMap.set(category.id, {
                ...category,
                updatedAt: new Date().toISOString(),
              });
              updatedCategoriesCount++;
            } else {
              categoriesMap.set(category.id, category);
              addedCategoriesCount++;
            }
          });

          const newCategories = Array.from(categoriesMap.values());
          setCategories(newCategories);
          await saveCategories(newCategories);

          if (addedCategoriesCount > 0 || updatedCategoriesCount > 0) {
            importResults.push(`分类：新增 ${addedCategoriesCount} 个，更新 ${updatedCategoriesCount} 个`);
          }
        }
      }

      // 处理提示词数据
      if (validPrompts.length >= 0) {
        let addedPromptsCount = 0;
        let updatedPromptsCount = 0;
        let deletedPromptsCount = 0;

        if (isRemoteSync) {
          // 远程同步：保留本地数据，删除旧的远程数据，添加新的远程数据
          const localPrompts = prompts.filter(p => p.source === 'local');
          const oldRemotePromptIds = prompts.filter(p => p.source === 'remote').map(p => p.id);
          const newRemotePromptIds = validPrompts.map(p => p.id);
          updatedPromptsCount = newRemotePromptIds.filter(id => oldRemotePromptIds.includes(id)).length;
          if(newRemotePromptIds.length > oldRemotePromptIds.length) {
            addedPromptsCount = newRemotePromptIds.length - oldRemotePromptIds.length;
          } else {
            deletedPromptsCount = oldRemotePromptIds.length - newRemotePromptIds.length;
          }

          const promptsMap = new Map(localPrompts.map(p => [p.id, p]));

          validPrompts.forEach(prompt => {
            promptsMap.set(prompt.id, prompt);
          });

          const newPrompts = Array.from(promptsMap.values());
          setPrompts(newPrompts);
          await savePrompts(newPrompts);

          if (addedPromptsCount > 0 || updatedPromptsCount > 0 || deletedPromptsCount > 0) {
            const parts = [];
            if (addedPromptsCount > 0) parts.push(`新增 ${addedPromptsCount} 个`);
            if (updatedPromptsCount > 0) parts.push(`更新 ${updatedPromptsCount} 个`);
            if (deletedPromptsCount > 0) parts.push(`删除 ${deletedPromptsCount} 个`);
            importResults.push(`提示词：${parts.join('，')}`);
          }
        } else {
          // 本地导入：普通合并逻辑
          const promptsMap = new Map(prompts.map(p => [p.id, p]));

          validPrompts.forEach(prompt => {
            if (promptsMap.has(prompt.id)) {
              promptsMap.set(prompt.id, {
                ...prompt,
                lastModified: new Date().toISOString(),
              });
              updatedPromptsCount++;
            } else {
              promptsMap.set(prompt.id, prompt);
              addedPromptsCount++;
            }
          });

          const newPrompts = Array.from(promptsMap.values());
          setPrompts(newPrompts);
          await savePrompts(newPrompts);

          if (addedPromptsCount > 0 || updatedPromptsCount > 0) {
            importResults.push(`提示词：新增 ${addedPromptsCount} 个，更新 ${updatedPromptsCount} 个`);
          }
        }
      }

      // 显示成功提示
      if (importResults.length >= 0) {
        toast.success(`${isRemoteSync ? '同步' : '导入'}成功！${importResults.join('，')}`, 5000);
      } else {
        toast.info('没有找到新的数据', 3000);
      }

      // 清理状态
      setPendingImportData(null);
      setIsImportConfirmOpen(false);

    } catch (err) {
      console.error(`${pendingImportData.isRemoteSync ? '同步' : '导入'}错误:`, err);
      setError(`${pendingImportData.isRemoteSync ? '同步' : '导入'}失败: ${err instanceof Error ? err.message : '未知错误'}`);
    }
  };

  // 清空所有数据
  const handleClearAllData = async () => {
    if (prompts.length === 0 && categories.length === 0) {
      toast.warning('没有数据可清空', 3000);
      return;
    }

    const shouldClear = window.confirm(
      `确认清空所有数据吗？\n\n这将删除：\n• ${prompts.length} 个提示词\n• ${categories.length} 个分类\n\n此操作不可恢复！`
    );

    if (!shouldClear) {
      return;
    }

    try {
      // 清空所有数据
      await storageManager.clearAllData();

      // 重置状态
      setPrompts([]);
      setCategories([]);
      setSelectedCategoryId(null);
      setSelectedFilterTags([]);
      setSelectedSourceFilter('all');
      setSearchTerm('');

      toast.success('所有数据已清空', 3000);
    } catch (err) {
      console.error('清空数据错误:', err);
      setError('清空数据失败');
    }
  };

  const localPromptsCount = prompts.filter(p => (p.source || 'local') === 'local').length;
  const remotePromptsCount = prompts.filter(p => p.source === 'remote').length;
  const sourceFilterOptions: Array<{ value: SourceFilter; label: string; count: number }> = [
    { value: 'all', label: '全部来源', count: prompts.length },
    { value: 'local', label: '本地创建', count: localPromptsCount },
    { value: 'remote', label: '远程同步', count: remotePromptsCount },
  ];
  const selectedSourceLabel = sourceFilterOptions.find(option => option.value === selectedSourceFilter)?.label || '全部来源';
  const hasActiveFilters = Boolean(searchTerm || selectedCategoryId || selectedFilterTags.length > 0 || selectedSourceFilter !== 'all');

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
        <div className="flex justify-center items-center min-h-screen">
          <div className="text-center space-y-4">
            <div className="relative">
              <div className="w-16 h-16 mx-auto">
                <div className="absolute inset-0 border-4 border-blue-200 dark:border-blue-800 rounded-full"></div>
                <div className="absolute inset-0 border-4 border-blue-600 dark:border-blue-400 rounded-full border-t-transparent animate-spin"></div>
              </div>
            </div>
            <div className="space-y-2">
              <h3 className="text-lg font-semibold text-gray-700 dark:text-gray-300">加载数据中</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">请稍候...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-10">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="space-y-4">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                  <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 9l3 3-3 3m5 0h3" />
                  </svg>
                </div>
                <h1 className="text-4xl font-bold bg-gradient-to-r from-gray-900 via-blue-900 to-indigo-900 dark:from-gray-100 dark:via-blue-100 dark:to-indigo-100 bg-clip-text text-transparent">
                  提示词库
                </h1>
              </div>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl">
                管理您的提示词库，快速查找和使用提示词
              </p>

              {/* 统计卡片 */}
              <div className="flex flex-wrap gap-4">
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">总计 {prompts.length}</span>
                  </div>
                </div>
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">已启用 {prompts.filter(p => p.enabled !== false).length}</span>
                  </div>
                </div>
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">本地 {localPromptsCount}</span>
                  </div>
                </div>
                <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center space-x-2">
                    <div className="w-2 h-2 bg-indigo-500 rounded-full"></div>
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">远程 {remotePromptsCount}</span>
                  </div>
                </div>
                {selectedCategoryId && (
                  <div className="bg-white/70 dark:bg-gray-800/70 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl px-4 py-3 shadow-sm">
                    <div className="flex items-center space-x-2">
                      <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        当前分类 {filteredPrompts.length}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* 导入导出按钮 */}
            <div className="flex items-center gap-2">
              <button
                onClick={exportPrompts}
                disabled={prompts.length === 0 && categories.length === 0}
                className="inline-flex items-center px-3 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md disabled:transform-none disabled:shadow-none"
                title={prompts.length === 0 && categories.length === 0 ? '没有数据可导出' : '导出所有数据'}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
                </svg>
                导出
              </button>

              <div className="relative group">
                <button
                  onClick={triggerFileInput}
                  className="inline-flex items-center px-3 py-2 bg-purple-600 hover:bg-purple-700 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md"
                  title="支持导入分类和提示词数据"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  本地导入
                </button>

                {/* 本地导入格式提示 */}
                <div className="absolute top-full left-0 mt-2 w-96 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    <div className="font-medium mb-3">📋 支持的文件内容格式如下：</div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-3 mb-3 font-mono text-xs overflow-hidden">
                      <pre className="whitespace-pre-wrap break-words">{`{
  "categories": [
    {
      "id": "programming",
      "name": "编程开发",
      "description": "编程、代码相关的提示词",
      "color": "#10b981",
      "enabled": true,
      "createdAt": "2025-09-26T05:46:45.543Z",
      "updatedAt": "2025-09-26T06:15:56.122Z"
    }
  ],
  "prompts": [
    {
      "id": "default-code-prompt",
      "title": "Cursor User Rule",
      "content": "xxxxx",
      "tags": ["Cursor"],
      "notes": "xxxx",
      "enabled": true,
      "categoryId": "programming",
      "lastModified": "2025-09-26T06:12:22.098Z",
      "sortOrder": 1
    }
  ],
  "exportDate": "2025-09-26T06:16:18.995Z",
  "version": "1.0"
}`}</pre>
                    </div>
                    <div className="text-amber-600 dark:text-amber-400 font-medium">⚠️ 相同ID的数据将被更新</div>
                  </div>
                </div>
              </div>

              <div className="relative group">
                <button
                  onClick={syncFromRemoteApi}
                  disabled={isRemoteSyncing}
                  className="inline-flex items-center px-3 py-2 bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md disabled:cursor-not-allowed"
                  title="从固定API地址同步最新数据"
                >
                  {isRemoteSyncing ? (
                    <>
                      <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      同步中...
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                      </svg>
                      远程同步
                    </>
                  )}
                </button>

                {/* 远程同步提示 */}
                <div className="absolute top-full right-0 mt-2 w-96 p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                  <div className="text-xs text-gray-600 dark:text-gray-300">
                    <div className="font-medium mb-3">🔄 远程同步说明：</div>
                    <div className="space-y-2 mb-3">
                      <div className="flex items-start space-x-2">
                        <span className="text-indigo-500 mt-0.5">•</span>
                        <span>从固定API地址同步最新数据</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-indigo-500 mt-0.5">•</span>
                        <span>自动获取远程的分类和提示词</span>
                      </div>
                      <div className="flex items-start space-x-2">
                        <span className="text-indigo-500 mt-0.5">•</span>
                        <span>同步前会显示确认对话框</span>
                      </div>
                    </div>
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-md p-2 mb-2 font-mono text-xs break-all">
                      <span className="text-gray-500">API地址：</span><br/>
                      {REMOTE_SYNC_API}
                    </div>
                    <div className="text-amber-600 dark:text-amber-400 font-medium">⚠️ 相同ID的数据将被覆盖</div>
                  </div>
                </div>
              </div>

              {/* 清空数据按钮 */}
              <button
                onClick={handleClearAllData}
                disabled={prompts.length === 0 && categories.length === 0}
                className="inline-flex items-center px-3 py-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-300 text-white text-sm font-medium rounded-lg transition-all duration-200 hover:shadow-md disabled:transform-none disabled:shadow-none"
                title={prompts.length === 0 && categories.length === 0 ? '没有数据可清空' : '清空所有数据'}
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                清空
              </button>
            </div>
          </div>
        </div>

        {/* 错误提示 */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-2xl p-4 shadow-sm">
            <div className="flex items-start space-x-3">
              <div className="flex-shrink-0">
                <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                  <svg className="w-5 h-5 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
              </div>
              <div className="flex-1">
                <h3 className="text-sm font-medium text-red-800 dark:text-red-300">操作失败</h3>
                <p className="text-sm text-red-700 dark:text-red-400 mt-1">{error}</p>
              </div>
              <button
                onClick={() => setError(null)}
                className="flex-shrink-0 text-red-400 dark:text-red-500 hover:text-red-600 dark:hover:text-red-300 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>
        )}

        {/* 操作栏 */}
        <div className="mb-8">
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl p-4 shadow-xl">
            <div className="flex flex-col xl:flex-row gap-4 xl:gap-6 items-stretch xl:items-center">
              {/* 搜索和筛选区域 */}
              <div className="flex-1 flex flex-col sm:flex-row sm:flex-wrap gap-3 w-full min-w-0">
                <div className="flex-1 min-w-0">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-600 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      placeholder="搜索提示词..."
                      className="block xl:w-96 w-full pl-10 pr-3 py-3 border border-gray-200 dark:border-gray-600 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-gray-100 placeholder-gray-500 dark:placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
                    />
                  </div>
                </div>

                <div className="w-full sm:w-auto sm:min-w-[160px] xl:w-40">
                  <div className="relative">
                    <select
                      value={selectedCategoryId || ""}
                      onChange={(e) => setSelectedCategoryId(e.target.value || null)}
                      className="flex items-center w-full h-[48px] px-4 pr-8 border border-gray-200 dark:border-gray-600 rounded-xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm text-gray-900 dark:text-gray-100 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200 appearance-none cursor-pointer"
                    >
                      <option value="">全部分类</option>
                      {categories.map((category) => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                    <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                      <svg className="h-4 w-4 text-gray-500 dark:text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>

                <div className="w-full sm:w-auto sm:flex-shrink-0">
                  <div className="flex h-[48px] rounded-xl border border-gray-200 dark:border-gray-600 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm p-1">
                    {sourceFilterOptions.map((option) => {
                      const isActive = selectedSourceFilter === option.value;

                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => setSelectedSourceFilter(option.value)}
                          className={`min-w-[86px] px-3 rounded-lg text-sm font-medium transition-all duration-200 ${
                            isActive
                              ? 'bg-blue-600 text-white shadow-sm'
                              : 'text-gray-600 dark:text-gray-300 hover:bg-white dark:hover:bg-gray-700'
                          }`}
                          title={`${option.label}：${option.count} 条`}
                        >
                          <span>{option.label}</span>
                          <span className={`ml-1 text-xs ${isActive ? 'text-blue-100' : 'text-gray-400 dark:text-gray-500'}`}>
                            {option.count}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* 标签过滤器 */}
                <div className="w-full sm:w-auto xl:w-64">
                  <TagSelector
                    selectedTags={selectedFilterTags}
                    onChange={setSelectedFilterTags}
                    predefinedTags={getAllTags()}
                    placeholder="按标签过滤..."
                  />
                </div>
              </div>

              <div className="w-full xl:w-auto flex justify-end xl:flex-shrink-0">
                <button
                  onClick={openAddModal}
                  className="inline-flex min-w-[132px] flex-shrink-0 items-center justify-center whitespace-nowrap px-6 py-2.5 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white text-sm font-medium rounded-xl transition-all duration-200 hover:shadow-lg hover:-translate-y-0.5 shadow-blue-500/25"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  新增提示词
                </button>

                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={importPrompts}
                  accept=".json"
                  className="hidden"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Prompts列表 */}
        <PromptList
          prompts={filteredPrompts}
          onEdit={startEdit}
          onDelete={deletePrompt}
          onReorder={handleReorder}
          onToggleEnabled={togglePromptEnabled}
          onTogglePinned={togglePromptPinned}
        />

        {/* 无结果提示 */}
        {filteredPrompts.length === 0 && (
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border border-white/20 dark:border-gray-700/50 rounded-2xl shadow-xl p-6">
            {hasActiveFilters ? (
              <div className='text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg'>
                <svg
                  className='mx-auto h-12 w-12 text-gray-400 dark:text-gray-500'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden='true'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z'
                  />
                </svg>
                <p className='mt-4 text-lg font-medium text-gray-700 dark:text-gray-300'>未找到匹配的提示词</p>
                <p className='mt-2 text-gray-500 dark:text-gray-400'>
                  当前筛选条件下没有结果
                  {selectedCategoryId ? `，分类：${categories.find(c => c.id === selectedCategoryId)?.name || ''}` : ''}
                  {selectedSourceFilter !== 'all' ? `，来源：${selectedSourceLabel}` : ''}
                  {searchTerm ? `，关键词：${searchTerm}` : ''}
                  {selectedFilterTags.length > 0 ? `，标签：${selectedFilterTags.join(', ')}` : ''}
                </p>
                <div className="flex flex-wrap justify-center gap-3 mt-4">
                  {searchTerm && (
                    <button
                      onClick={() => setSearchTerm("")}
                      className="inline-flex items-center px-4 py-2 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                      清除搜索
                    </button>
                  )}
                  {selectedFilterTags.length > 0 && (
                    <button
                      onClick={() => setSelectedFilterTags([])}
                      className="inline-flex items-center px-4 py-2 bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 rounded-lg hover:bg-green-100 dark:hover:bg-green-900/50 transition-colors font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                      </svg>
                      清除标签过滤
                    </button>
                  )}
                  {selectedCategoryId && (
                    <button
                      onClick={() => setSelectedCategoryId(null)}
                      className="inline-flex items-center px-4 py-2 bg-purple-50 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 rounded-lg hover:bg-purple-100 dark:hover:bg-purple-900/50 transition-colors font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
                      查看所有分类
                    </button>
                  )}
                  {selectedSourceFilter !== 'all' && (
                    <button
                      onClick={() => setSelectedSourceFilter('all')}
                      className="inline-flex items-center px-4 py-2 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 rounded-lg hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition-colors font-medium"
                    >
                      <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h18M6 12h12M10 17h4" />
                      </svg>
                      查看全部来源
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div className='text-center py-12 bg-gray-50 dark:bg-gray-800 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-600'>
                <svg
                  className='mx-auto h-12 w-12 text-gray-400 dark:text-gray-500'
                  fill='none'
                  viewBox='0 0 24 24'
                  stroke='currentColor'
                  aria-hidden='true'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={1.5}
                    d='M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10'
                  />
                </svg>
                <p className='mt-4 text-lg font-medium text-gray-700 dark:text-gray-300'>还没有添加提示词</p>
                <p className='mt-2 text-gray-500 dark:text-gray-400'>点击"新增提示词"按钮创建您的第一个提示词</p>
              </div>
            )}
          </div>
        )}

        {/* 添加/编辑 Prompt 模态框 */}
        <Modal
          isOpen={isModalOpen}
          onClose={closeModal}
          title={editingPrompt ? '编辑提示词' : '新建提示词'}
        >
          <PromptForm
            onSubmit={handlePromptSubmit}
            initialData={
              editingPrompt
                ? {
                    ...editingPrompt,
                  }
                : initialContent
                ? {
                    id: "",
                    title: "",
                    content: initialContent,
                    tags: [],
                    enabled: true,
                    categoryId: DEFAULT_CATEGORY_ID,
                  }
                : null
            }
            onCancel={cancelEdit}
            isEditing={!!editingPrompt}
          />
        </Modal>

        {/* 导入确认弹窗 */}
        <ImportConfirmModal
          isOpen={isImportConfirmOpen}
          onClose={() => {
            setIsImportConfirmOpen(false);
            setPendingImportData(null);
          }}
          onConfirm={handleConfirmImport}
          title={pendingImportData?.isRemoteSync ? '确认远程同步' : '确认本地导入'}
          localCategoriesCount={categories.length}
          localPromptsCount={prompts.length}
          importCategoriesCount={pendingImportData?.categories.length || 0}
          importPromptsCount={pendingImportData?.prompts.length || 0}
          isRemoteSync={pendingImportData?.isRemoteSync || false}
          localSourceCategoriesCount={categories.filter(c => c.source === 'local').length}
          localSourcePromptsCount={prompts.filter(p => p.source === 'local').length}
          remoteSourceCategoriesCount={categories.filter(c => c.source === 'remote').length}
          remoteSourcePromptsCount={prompts.filter(p => p.source === 'remote').length}
        />

        {/* 确认删除对话框 */}
        <ConfirmModal
          isOpen={isConfirmModalOpen}
          onClose={() => {
            setIsConfirmModalOpen(false);
            setPromptToDelete(null);
          }}
          onConfirm={handleConfirmDelete}
          title="确认删除"
          message=""
          confirmText="删除"
          cancelText="取消"
        />
      </div>
    </div>
  );
};

export default PromptManager;
